import { uniq } from 'lodash';

import { FilterOption } from 'src/plp/components/PlpFilters/PlpFilters.type';
import {
  mapBodyTypeFilter,
  mapDriveTypeFilter,
  mapFilterDataRowFilter,
  mapMakeAndModelFilter,
} from 'src/plp/components/PlpFilters/plpFiltersMap';
import { PLP_BASE_PATH } from 'src/plp/helpers/getPlpSlug';
import { filterOrder } from 'src/plp/helpers/getPlpUrlWithFilter';
import { FilterListPayload, FiltersPayloadRow } from 'src/types/CarFilters.types';
import { FilterValues } from 'src/types/CataloguePage.types';

const mapFilterValuesToFilterListKeys = new Map<FilterValues, keyof FilterListPayload>([
  [FilterValues.Make, 'makeAndModel'],
  [FilterValues.Model, 'makeAndModel'],
  [FilterValues.Seats, 'seats'],
  [FilterValues.Doors, 'doors'],
  [FilterValues.Transmission, 'transmission'],
  [FilterValues.FuelType, 'fuelType'],
  [FilterValues.BodyType, 'bodyType'],
  [FilterValues.BodySize, 'bodyType'],
  [FilterValues.DriveType, 'driveType'],
  [FilterValues.HighlightedFeatures, 'highlightedFeatures'],
  [FilterValues.Colour, 'colour'],
  [FilterValues.Ancap, 'ancapSafetyRating'],
  [FilterValues.Co2emissions, 'co2Emissions'],
  [FilterValues.State, 'state'],
]);

const rangeFilterValues = [
  FilterValues.YearMax,
  FilterValues.YearMin,
  FilterValues.CashMax,
  FilterValues.CashMin,
  FilterValues.FinanceMax,
  FilterValues.FinanceMin,
  FilterValues.KMMax,
  FilterValues.KMMin,
];

const handleRangeFilters = (
  filters: FiltersPayloadRow[],
  filterSlugName: string,
  minRangeName: FilterValues,
  maxRangeName: FilterValues,
) => {
  let slug = '';
  const minFilter = filters.find((item) => item?.filter === minRangeName);
  const maxFilter = filters.find((item) => item?.filter === maxRangeName);

  if (minFilter && maxFilter) {
    slug = `${filterSlugName}-${minFilter?.value}-${maxFilter?.value}`;
  } else if (minFilter) {
    slug = `${filterSlugName}-from-${minFilter.value}`;
  } else if (maxFilter) {
    slug = `${filterSlugName}-under-${maxFilter.value}`;
  }

  return filters.map((item) => {
    return {
      value: String(item.value),
      filter: item.filter,
      slug: slug,
    };
  });
};

const filterFilterOptionByValue = (filterOptions: FilterOption[], value: string) =>
  filterOptions.filter((option) => option.value === value);

const mapStateFilter = (value: string): FilterOption[] => {
  return [
    {
      value: value,
      filter: FilterValues.State,
      slug: value,
      label: value,
    },
  ];
};

export const filtersPayloadRowsToPlpFilterOptions = (
  filters: FiltersPayloadRow[],
  filterList: FilterListPayload,
): FilterOption[] => {
  const rangeFilters: FiltersPayloadRow[] = filters.filter((item) => rangeFilterValues.includes(item.filter));
  const nonRangeFilters = filters.filter((item) => !rangeFilterValues.includes(item.filter));

  const cashFilters = rangeFilters.filter(
    (item) => item.filter === FilterValues.CashMin || item.filter === FilterValues.CashMax,
  );
  const financeFilters = rangeFilters.filter(
    (item) => item.filter === FilterValues.FinanceMin || item.filter === FilterValues.FinanceMax,
  );
  const yearFilters = rangeFilters.filter(
    (item) => item.filter === FilterValues.YearMin || item.filter === FilterValues.YearMax,
  );
  const kmFilters = rangeFilters.filter(
    (item) => item.filter === FilterValues.KMMin || item.filter === FilterValues.KMMax,
  );

  const cashFilterOptions: FilterOption[] = handleRangeFilters(
    cashFilters,
    'price',
    FilterValues.CashMin,
    FilterValues.CashMax,
  );
  const financeFilterOptions: FilterOption[] = handleRangeFilters(
    financeFilters,
    'finance',
    FilterValues.FinanceMin,
    FilterValues.FinanceMax,
  );
  const yearFilterOptions: FilterOption[] = handleRangeFilters(
    yearFilters,
    'year',
    FilterValues.YearMin,
    FilterValues.YearMax,
  );
  const kmFilterOptions: FilterOption[] = handleRangeFilters(kmFilters, 'km', FilterValues.KMMin, FilterValues.KMMax);

  const nonRangeFilterOptions: FilterOption[] = nonRangeFilters
    .map((item) => {
      const filterListKey = mapFilterValuesToFilterListKeys.get(item.filter);
      switch (filterListKey) {
        case 'makeAndModel':
          return filterFilterOptionByValue(mapMakeAndModelFilter(filterList.makeAndModel), String(item.value));
        case 'bodyType':
          return filterFilterOptionByValue(mapBodyTypeFilter(filterList.bodyType), String(item.value));
        case 'driveType':
          return filterFilterOptionByValue(mapDriveTypeFilter(filterList.driveType), String(item.value));
        case 'seats':
          return filterFilterOptionByValue(
            mapFilterDataRowFilter(filterList.seats, FilterValues.Seats),
            String(item.value),
          );
        case 'doors':
          return filterFilterOptionByValue(
            mapFilterDataRowFilter(filterList.doors, FilterValues.Doors),
            String(item.value),
          );
        case 'transmission':
          return filterFilterOptionByValue(
            mapFilterDataRowFilter(filterList.transmission, FilterValues.Transmission),
            String(item.value),
          );
        case 'fuelType':
          return filterFilterOptionByValue(
            mapFilterDataRowFilter(filterList.fuelType, FilterValues.FuelType),
            String(item.value),
          );
        case 'highlightedFeatures':
          return filterFilterOptionByValue(
            mapFilterDataRowFilter(filterList.highlightedFeatures, FilterValues.HighlightedFeatures),
            String(item.value),
          );
        case 'colour':
          return filterFilterOptionByValue(
            mapFilterDataRowFilter(filterList.colour, FilterValues.Colour),
            String(item.value),
          );
        case 'ancapSafetyRating':
          return filterFilterOptionByValue(
            mapFilterDataRowFilter(filterList.ancapSafetyRating, FilterValues.Ancap),
            String(item.value),
          );
        case 'co2Emissions':
          return filterFilterOptionByValue(
            mapFilterDataRowFilter(filterList.co2Emissions, FilterValues.Co2emissions),
            String(item.value),
          );
        case 'state':
          return mapStateFilter(String(item.value));
        default:
          return [];
      }
    })
    .flat();

  return [
    ...nonRangeFilterOptions,
    ...cashFilterOptions,
    ...financeFilterOptions,
    ...yearFilterOptions,
    ...kmFilterOptions,
  ];
};

export const generateUrlFromFilterOptions = (filterOptions: FilterOption[]): string => {
  const filterOptionsWithOrder = filterOptions.map((option) => {
    return {
      ...option,
      order: filterOrder.findIndex((filter) => filter === option.filter),
    };
  });
  filterOptionsWithOrder.sort((a, b) => a.order - b.order);

  const combinedSlug = uniq(filterOptionsWithOrder.map((option) => option.slug)).join('_');
  return `${PLP_BASE_PATH}/${combinedSlug}`;
};
